import { useEffect, useState } from "react";
import { App } from "@capacitor/app";
import { bs, useBoundStore } from "state/bound.store";
import { spLogTags } from "@eventr/common";
// Was the internal deep path "@mui/material/styles/styled", which v6 stopped exposing.
import { styled } from "@mui/material/styles";
import { ColoursObj } from "@eventr/common-storybook";
import { expenseLocalStore } from "./local-expenses";
import { DataAPI } from "data/Data";
import { getModifiedTime } from "../expense-utils";
import { useShallow } from "zustand/react/shallow";
import { useConnectivityStore } from "feat/connectivity/connectivity.store";

type SyncStatus = "pending" | "success" | "failed";

export default function LocalExpenseManager() {
  const [showSyncBanner, setShowSyncBanner] = useState(false);
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);

  const { fetchUserExpenses } = useBoundStore(
    useShallow((s) => ({
      fetchUserExpenses: s.fetchUserExpenses,
    })),
  );

  useEffect(() => {
    const addAppListener = async () => {
      return await App.addListener("appStateChange", async (state) => {
        if (state.isActive) {
          if (!useConnectivityStore.getState().isConnectivityAvailable) return;
          //get local saved expenses
          const unsyncedExpenses = await expenseLocalStore.getByOrg(bs().selectedOrg?.name ?? "");
          spLogTags(`[manager] Found ${unsyncedExpenses.length} offline expenses`, ["expenses"]);

          if (unsyncedExpenses.length === 0) return;

          await fetchUserExpenses();
          //set status for each unsynced expense to 0
          setSyncStatuses(unsyncedExpenses.map(() => "pending"));
          setShowSyncBanner(true);

          //loop through each one
          for (const [idx, unsyncedExpense] of unsyncedExpenses.entries()) {
            let succeeded = true;
            const { itineraryId, cost, syncAction } = unsyncedExpense.data;
            //see if this expense exists as a remote one too
            const entityId = cost.entity_id ?? "";
            const expenseInBoundStore = bs().expenses[itineraryId]?.[cost._id];

            spLogTags(`[manager] expenseInBoundStore ${expenseInBoundStore}`, ["expenses"]);

            const modifiedRemote = getModifiedTime(expenseInBoundStore?.cost);
            const modifiedLocal = getModifiedTime(unsyncedExpense.data?.cost);

            //if we find the expense in the list of remotes expenses and it has been modified more recently
            const remoteMoreRecentThanLocal = modifiedRemote > modifiedLocal;

            if (remoteMoreRecentThanLocal) {
              await expenseLocalStore.remove(unsyncedExpense.data.cost._id);
              spLogTags(`[manager] Deleted local expense nothing to action`, ["expenses"]);

              setSyncStatuses((existing) => {
                const updated = [...existing];
                updated[idx] = "success";
                return updated;
              });
              continue;
            }

            spLogTags(`[manager] Attempting sync action for ${cost._id} expense, incrementing retry counter`, [
              "expenses",
            ]);
            await expenseLocalStore.incrementRetry(unsyncedExpense);

            //do edit add or delete action
            let response;
            try {
              switch (syncAction) {
                case "add":
                  spLogTags(`[manager] Adding expense ${cost._id}`, ["expenses"]);
                  response = await DataAPI.expenses.addExpenses(itineraryId, { [entityId]: { costs: [cost] } });
                  break;
                case "edit":
                  spLogTags(`[manager] Editing expense ${cost._id}`, ["expenses"]);
                  response = await DataAPI.expenses.editExpenses(itineraryId, { [entityId]: { costs: [cost] } });
                  break;
                case "delete":
                  spLogTags(`[manager] Deleting expense ${cost._id}`, ["expenses"]);
                  response = await DataAPI.expenses.deleteExpenses(itineraryId, { [entityId]: { costs: [cost] } });
                  break;
              }

              if (response?.status === "error") throw new Error(`Error ${syncAction} expense ${response.message}`);

              const costFromResponse = Object.values(response.data ?? {})
                .flatMap((costing) => costing.costs)
                .find((cost) => cost._id === unsyncedExpense.data.cost._id);

              if (!costFromResponse) throw new Error(`Unable to find cost in add response`);
              spLogTags(
                `[manager] Add Edit or Delete was successful for ${cost._id} expense, removing from local-expense-store`,
                ["expenses"],
              );
              expenseLocalStore.remove(cost._id);
              //use backend reponse here
              bs().upsertExpense({ cost: costFromResponse }, itineraryId);
            } catch (err) {
              spLogTags(`[manager] Failed to sync ${cost._id} (action: ${syncAction}): ${err}`, ["expenses"]);
              succeeded = false;
            }

            setSyncStatuses((existing) => {
              const updated = [...existing];
              updated[idx] = succeeded ? "success" : "failed";
              return updated;
            });
          }
          setTimeout(() => setShowSyncBanner(false), 3000);
        }
      });
    };

    const appListener = addAppListener();

    return () => {
      appListener.then((res) => res.remove());
    };
  }, [fetchUserExpenses]);

  function getStatusColour(status: SyncStatus) {
    switch (status) {
      case "success":
        return ColoursObj.states.success;
      case "failed":
        return ColoursObj.states.error;
      default:
        return ColoursObj.core.secondary;
    }
  }

  return (
    <>
      {showSyncBanner && (
        <SyncProgressContainer>
          {syncStatuses.map((status, idx) => {
            return <SyncProgress key={idx} style={{ backgroundColor: getStatusColour(status) }} />;
          })}
        </SyncProgressContainer>
      )}
    </>
  );
}

const SyncProgress = styled("div")({
  width: "100%",
});

const SyncProgressContainer = styled("div")({
  position: "absolute",
  height: "3px",
  width: "var(--app-width)",
  zIndex: "601",
  display: "flex",
});
